import { useState } from 'react';
import { MessageCircle, Share2, Mail, Link2, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/context/ThemeContext';

const Integrations = () => {
  const { isDark } = useTheme();
  const [integrations] = useState([
    {
      id: 'whatsapp',
      name: 'WhatsApp Channel',
      description: 'Connect WhatsApp channel for notifications and updates',
      icon: MessageCircle,
      status: 'connected',
      link: 'https://wa.me/1234567890',
    },
    {
      id: 'social',
      name: 'Social Media Sharing',
      description: 'Enable social sharing for projects and problem statements',
      icon: Share2,
      status: 'connected',
      platforms: ['Twitter', 'LinkedIn', 'Facebook'],
    },
    {
      id: 'email',
      name: 'Email Digests',
      description: 'Send weekly email digests to subscribers',
      icon: Mail,
      status: 'disconnected',
      subscribers: 0,
    },
  ]);

  const handleToggle = (id) => {
    // TODO: Implement toggle integration
    console.log('Toggling integration:', id);
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <h2 className={`text-2xl sm:text-3xl font-bold mb-2 transition-colors ${
          isDark ? 'text-white' : 'text-gray-900'
        }`}>Integrations</h2>
        <p className={`text-sm sm:text-base ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Manage WhatsApp, social sharing, and email integrations</p>
      </div>

      {/* Integrations Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {integrations.map((integration) => {
          const Icon = integration.icon;
          return (
            <div
              key={integration.id}
              className={`rounded-xl p-6 shadow-md border transition-colors flex flex-col ${
                isDark ? 'bg-gradient-to-br from-[#2d1b4e] to-[#1a0b2e] border-purple-500/20 hover:border-purple-500/40' : 'bg-white border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className={`p-3 rounded-lg transition-colors ${
                      integration.status === 'connected'
                        ? isDark
                          ? 'bg-green-500/30'
                          : 'bg-green-100'
                        : isDark
                          ? 'bg-gray-500/30'
                          : 'bg-gray-100'
                    }`}
                  >
                    <Icon
                      className={`w-6 h-6 transition-colors ${
                        integration.status === 'connected'
                          ? isDark
                            ? 'text-green-400'
                            : 'text-green-600'
                          : isDark
                            ? 'text-gray-400'
                            : 'text-gray-600'
                      }`}
                    />
                  </div>
                  <div>
                    <h3 className={`font-semibold transition-colors ${
                      isDark ? 'text-white' : 'text-gray-900'
                    }`}>{integration.name}</h3>
                    <p className={`text-xs mt-1 transition-colors ${
                      isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>{integration.description}</p>
                  </div>
                </div>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 transition-colors ${
                    integration.status === 'connected'
                      ? isDark
                        ? 'bg-green-500/30 text-green-200'
                        : 'bg-green-100 text-green-700'
                      : isDark
                        ? 'bg-red-500/30 text-red-200'
                        : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {integration.status === 'connected' ? (
                    <Check className="w-3 h-3" />
                  ) : (
                    <X className="w-3 h-3" />
                  )}
                  {integration.status === 'connected' ? 'Connected' : 'Disconnected'}
                </span>
              </div>

              {/* Integration Details */}
              <div className="space-y-3 mb-4 flex-grow">
                {integration.link && (
                  <div className="flex items-center gap-2 text-sm">
                    <Link2 className={`w-4 h-4 transition-colors ${
                      isDark ? 'text-gray-400' : 'text-gray-400'
                    }`} />
                    <a
                      href={integration.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`transition-colors ${
                        isDark 
                          ? 'text-blue-400 hover:text-blue-300 hover:underline' 
                          : 'text-blue-600 hover:underline'
                      }`}
                    >
                      {integration.link}
                    </a>
                  </div>
                )}
                {integration.platforms && (
                  <div className="text-sm">
                    <p className={`mb-1 transition-colors ${
                      isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}>Platforms:</p>
                    <div className="flex flex-wrap gap-1">
                      {integration.platforms.map((platform, idx) => (
                        <span
                          key={idx}
                          className={`px-2 py-1 rounded text-xs transition-colors ${
                            isDark
                              ? 'bg-blue-500/30 text-blue-200'
                              : 'bg-blue-100 text-blue-700'
                          }`}
                        >
                          {platform}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {integration.subscribers !== undefined && (
                  <div className="text-sm">
                    <p className={`transition-colors ${
                      isDark ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      Subscribers: <span className={`font-medium transition-colors ${
                        isDark ? 'text-white' : 'text-gray-900'
                      }`}>{integration.subscribers}</span>
                    </p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-2 items-center mt-auto">
                <Button
                  variant="outline"
                  size="sm"
                  className={`flex-1 h-9 ${
                    isDark 
                      ? 'border-purple-500/30 text-purple-200 hover:bg-purple-500/20 hover:text-white' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                  onClick={() => handleToggle(integration.id)}
                >
                  {integration.status === 'connected' ? 'Disconnect' : 'Connect'}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className={`flex-1 h-9 ${
                    isDark 
                      ? 'border-purple-500/30 text-purple-200 hover:bg-purple-500/20 hover:text-white' 
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  Configure
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Integrations;

